import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  loadBusinessSettings,
  saveBusinessSettings,
} from '../utils/businessSettingsHelper';
import { calculateSupportStaffCostPerMinute } from '../utils/supportStaffCostCalculator';
import './BusinessSettings.css';
import { staffService } from '../services';
import api from '../services/api';
import SimpleTradingHours from './SimpleTradingHours';

// Converts regularity string to annual multiplier
const REGULARITY_MULTIPLIERS = {
  weekly: 52,
  monthly: 12,
  quarterly: 4,
  annually: 1,
  yearly: 1,
};

const BusinessSettings = () => {
  const navigate = useNavigate();
  
  const [settings, setSettings] = useState({
    monthly_profit_target: '',
    annual_overhead: 0,
    weeks_per_year: '52',
    staff_utilization_rate: '',
    total_working_days: 0,
    total_working_hours: 0,
    profit_level_1: '',
    profit_level_2: '',
    vat_rate: '20',
    is_vat_registered: false,
    annual_profit_target: 0,
    weekly_profit_target: 0,
    overhead_per_minute: 0,
    owner_profit_per_minute: 0,
    support_staff_per_minute: 0,
    total_annual_hours: 0,
    effective_hours: 0
  });
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [staffData, setStaffData] = useState([]);

  const isComplete = useMemo(() => {
    return !!(settings.monthly_profit_target && 
             settings.overhead_per_minute && 
             settings.owner_profit_per_minute && 
             settings.total_working_hours && 
             settings.profit_level_1 && 
             settings.profit_level_2);
  }, [
    settings.monthly_profit_target,
    settings.overhead_per_minute,
    settings.owner_profit_per_minute,
    settings.total_working_hours,
    settings.profit_level_1,
    settings.profit_level_2
  ]);

  // ─── FIX: Fetch live overhead rows and calculate real annual total ───────────
  // This replaces the cached annual_overhead from business_settings, which goes
  // stale whenever rows are added/deleted without the settings page being saved.
  const fetchLiveOverheadTotal = useCallback(async () => {
    try {
      const response = await api.get('/overhead');
      const overheads = Array.isArray(response.data) ? response.data : [];
      
      const total = overheads.reduce((sum, o) => {
        const multiplier = REGULARITY_MULTIPLIERS[o.regularity] ?? 12;
        return sum + (parseFloat(o.amount) || 0) * multiplier;
      }, 0);
      
      setSettings(prev => ({
        ...prev,
        annual_overhead: total
      }));

      return total;
    } catch (err) {
      console.error('Error fetching live overhead total:', err);
      return null; // Fall back to whatever is already in state
    }
  }, []);
  // ────────────────────────────────────────────────────────────────────────────

  const fetchStaffData = useCallback(async () => {
    try {
      const staffResponse = await staffService.getStaff();
      if (staffResponse && Array.isArray(staffResponse)) {
        setStaffData(staffResponse);
      }
    } catch (error) {
      console.error('Error fetching staff data:', error);
    }
  }, []);

  // Load settings on mount, then immediately overwrite annual_overhead with
  // the live value calculated from actual overhead rows.
  useEffect(() => {
    let mounted = true;
    
    const fetchSettings = async () => {
      try {
        setLoading(true);
        const businessSettings = await loadBusinessSettings();
        
        if (mounted && businessSettings && Object.keys(businessSettings).length > 0) {
          setSettings(prevSettings => ({
            ...prevSettings,
            ...businessSettings
          }));
        }
        
        if (mounted) {
          // Always fetch live overhead total — overrides cached business_settings value
          await fetchLiveOverheadTotal();
          await fetchStaffData();
        }
      } catch (err) {
        console.error('Error loading business settings:', err);
        if (mounted) {
          setError('Failed to load settings.');
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchSettings();
    
    return () => {
      mounted = false;
    };
  }, [fetchStaffData, fetchLiveOverheadTotal]);

  useEffect(() => {
    if (staffData.length > 0 && settings.weeks_per_year && parseFloat(settings.weeks_per_year) > 0) {
      try {
        const supportStaffPerMinute = calculateSupportStaffCostPerMinute(
          staffData, 
          parseFloat(settings.weeks_per_year)
        );
        
        if (supportStaffPerMinute > 0 && Math.abs(supportStaffPerMinute - settings.support_staff_per_minute) > 0.001) {
          setSettings(prev => ({
            ...prev,
            support_staff_per_minute: supportStaffPerMinute
          }));
        }
      } catch (error) {
        console.error('Error calculating support staff cost:', error);
      }
    }
  }, [staffData, settings.weeks_per_year]);

  // Recalculate derived values whenever inputs change.
  // annual_overhead now always reflects live overhead rows (set above),
  // so overhead_per_minute will be correct.
  useEffect(() => {
    const timer = setTimeout(() => {
      try {
        const monthlyProfit = parseFloat(settings.monthly_profit_target) || 0;
        const annualOverhead = parseFloat(settings.annual_overhead) || 0;
        const weeklyHours = parseFloat(settings.total_working_hours) || 0;
        const weeksPerYear = parseFloat(settings.weeks_per_year) || 52;
        const utilizationRate = parseFloat(settings.staff_utilization_rate) || 0;
        
        if (weeklyHours > 0 && weeksPerYear > 0) {
          const annualProfit = monthlyProfit * 12;
          const weeklyProfit = annualProfit / weeksPerYear;
          const annualHours = weeklyHours * weeksPerYear;
          const effectiveHours = annualHours * (utilizationRate / 100);
          
          const minutesPerYear = annualHours * 60;
          const ownerProfitPerMinute = minutesPerYear > 0 ? annualProfit / minutesPerYear : 0;
          const overheadPerMinute = minutesPerYear > 0 ? annualOverhead / minutesPerYear : 0;
          
          setSettings(prev => {
            const hasSignificantChanges = 
              Math.abs(prev.annual_profit_target - annualProfit) > 0.1 ||
              Math.abs(prev.weekly_profit_target - weeklyProfit) > 0.1 ||
              Math.abs(prev.total_annual_hours - annualHours) > 0.1 ||
              Math.abs(prev.effective_hours - effectiveHours) > 0.1 ||
              Math.abs(prev.owner_profit_per_minute - ownerProfitPerMinute) > 0.0001 ||
              Math.abs(prev.overhead_per_minute - overheadPerMinute) > 0.0001;
            
            if (hasSignificantChanges) {
              return {
                ...prev,
                annual_profit_target: annualProfit,
                weekly_profit_target: weeklyProfit,
                total_annual_hours: annualHours,
                effective_hours: effectiveHours,
                owner_profit_per_minute: ownerProfitPerMinute,
                overhead_per_minute: overheadPerMinute
              };
            }
            return prev;
          });
        }
      } catch (calcError) {
        console.error('Error in calculations:', calcError);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [
    settings.monthly_profit_target,
    settings.annual_overhead,
    settings.total_working_hours,
    settings.weeks_per_year,
    settings.staff_utilization_rate
  ]);

  const handleInputChange = useCallback((field, value) => {
    setSettings(prev => {
      if (prev[field] !== value) {
        return { ...prev, [field]: value };
      }
      return prev;
    });
  }, []);

  const handleTradingHoursChange = useCallback((hoursData) => {
    try {
      if (hoursData && typeof hoursData === 'object') {
        const newWorkingDays = hoursData.totalWorkingDays || 0;
        const newWorkingHours = hoursData.totalWorkingHours || 0;
        
        setSettings(prev => {
          const workingDaysChanged = prev.total_working_days !== newWorkingDays;
          const workingHoursChanged = Math.abs(prev.total_working_hours - newWorkingHours) > 0.01;
          
          if (workingDaysChanged || workingHoursChanged) {
            return {
              ...prev,
              total_working_days: newWorkingDays,
              total_working_hours: newWorkingHours
            };
          }
          return prev;
        });
      }
    } catch (error) {
      console.error('Error handling trading hours change:', error);
    }
  }, []);

  // ─── FIX: Fetch live overhead total before saving ────────────────────────────
  // Ensures we never write the old cached figure back into business_settings.
  const saveAllSettings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Re-fetch overhead total immediately before saving so the saved value
      // is always derived from actual overhead rows, not stale state.
      const liveOverheadTotal = await fetchLiveOverheadTotal();
      const annualOverhead = liveOverheadTotal !== null
        ? liveOverheadTotal
        : parseFloat(settings.annual_overhead) || 0;

      const settingsToSave = { ...settings, annual_overhead: annualOverhead };
      
      settingsToSave.monthly_profit = parseFloat(settings.monthly_profit_target);
      settingsToSave.annual_profit = parseFloat(settings.monthly_profit_target) * 12;
      
      const weeksPerYear = parseFloat(settings.weeks_per_year);
      settingsToSave.weekly_target_profit = settingsToSave.annual_profit / weeksPerYear;
      
      const weeklyHours = parseFloat(settings.total_working_hours);
      if (weeklyHours > 0 && weeksPerYear > 0) {
        const annualMinutes = weeklyHours * 60 * weeksPerYear;
        settingsToSave.owner_profit_per_minute = annualMinutes > 0 
          ? settingsToSave.annual_profit / annualMinutes 
          : 0;
        
        // Use live overhead total here, not stale state
        settingsToSave.overhead_per_minute = annualMinutes > 0 
          ? annualOverhead / annualMinutes 
          : 0;
      }
      
      if (staffData.length > 0 && settingsToSave.weeks_per_year) {
        const supportStaffPerMinute = calculateSupportStaffCostPerMinute(
          staffData, 
          parseFloat(settingsToSave.weeks_per_year)
        );
        settingsToSave.support_staff_per_minute = supportStaffPerMinute;
      }
      
      console.log('🚀 Attempting to save settings:', settingsToSave);
      
      try {
        await saveBusinessSettings(settingsToSave);
        
        setSettings(settingsToSave);
        
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
        
        if (isComplete) {
          setTimeout(() => navigate('/dashboard'), 1500);
        }
        
      } catch (apiError) {
        console.error('💥 Save failed with error:', apiError);
        
        let userErrorMessage = 'Failed to save settings. ';
        
        if (apiError.message && apiError.message.includes('Unexpected token')) {
          userErrorMessage += 'Server configuration issue - please contact support.';
        } else if (apiError.message && apiError.message.includes('Network Error')) {
          userErrorMessage += 'Network connection failed.';
        } else if (apiError.message && apiError.message.includes('404')) {
          userErrorMessage += 'API endpoint not found.';
        } else if (apiError.message && (apiError.message.includes('401') || apiError.message.includes('403'))) {
          userErrorMessage += 'Authentication failed - please refresh the page.';
        } else {
          userErrorMessage += apiError.message || 'Unknown error occurred.';
        }
        
        setError(userErrorMessage);
      }
      
    } catch (err) {
      console.error('💥 Unexpected error in saveAllSettings:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [settings, staffData, navigate, isComplete, fetchLiveOverheadTotal]);
  // ────────────────────────────────────────────────────────────────────────────

  const handleNavigateToOverheads = useCallback(() => {
    navigate('/calculator/overheads');
  }, [navigate]);

  // ─── FIX: Refresh overhead total when returning from the Overheads page ─────
  // Listens for window focus so that if the user opens overheads in the same tab
  // and comes back, the figure updates without a full page reload.
  useEffect(() => {
    const handleFocus = () => {
      fetchLiveOverheadTotal();
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [fetchLiveOverheadTotal]);
  // ────────────────────────────────────────────────────────────────────────────

  const SettingsCard = useMemo(() => ({ title, description, icon, children, complete = false }) => (
    <div className={`settings-card ${complete ? 'complete' : ''}`}>
      <div className="settings-card-header">
        <div className="settings-icon">{icon}</div>
        <div className="settings-status">
          {complete ? (
            <div className="status-complete">✅ Complete</div>
          ) : (
            <div className="status-pending">⚪ Pending</div>
          )}
        </div>
      </div>
      
      <h3>{title}</h3>
      <p>{description}</p>
      
      <div className="settings-content">
        {children}
      </div>
    </div>
  ), []);

  if (loading && Object.values(settings).every(value => 
    value === '' || value === 0 || value === false)
  ) {
    return <div className="loading">Loading business settings...</div>;
  }

  return (
    <div className="business-settings-container">
      <div className="settings-header">
        <h1>🏢 Business Settings</h1>
        <p className="settings-subtitle">
          Configure your business foundation for accurate pricing calculations
        </p>
        
        <div className="settings-progress">
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${isComplete ? 100 : 60}%` }}
            ></div>
          </div>
          <div className="progress-text">
            {isComplete ? '✅ Foundation Complete' : '⚠️ Setup Required'}
          </div>
        </div>
      </div>

      {!isComplete && (
        <div className="critical-setup-banner">
          <div className="banner-content">
            <div className="banner-icon">🚨</div>
            <div className="banner-text">
              <h3>CRITICAL FOR PRICING</h3>
              <p>These settings affect ALL service calculations. Complete this setup first for accurate pricing.</p>
            </div>
            <div className="banner-action">
              <span className="setup-required">Setup Required</span>
            </div>
          </div>
        </div>
      )}

      {isComplete && (
        <div className="success-banner">
          <div className="banner-content">
            <div className="banner-icon">🎉</div>
            <div className="banner-text">
              <h3>Business Foundation Complete!</h3>
              <p>Your settings are configured. All service calculations will be accurate.</p>
            </div>
          </div>
        </div>
      )}

      {error && <div className="error-message">{error}</div>}
      {saveSuccess && <div className="success-message">Settings saved successfully!</div>}

      <div className="settings-cards-grid">
        
        <SettingsCard
          title="💰 Profit & Revenue"
          description="Set your monthly profit targets and see how they translate to per-minute rates"
          icon="💰"
          complete={settings.monthly_profit_target && settings.owner_profit_per_minute}
        >
          <div className="card-form-group">
            <label>Monthly Profit Target (£) *</label>
            <input
              type="number"
              value={settings.monthly_profit_target}
              onChange={(e) => handleInputChange('monthly_profit_target', e.target.value)}
              placeholder="e.g. 5000"
            />
            <small>Your desired monthly profit (after taxes)</small>
          </div>
          
          {settings.monthly_profit_target && (
            <div className="calculation-preview">
              <div className="calc-row">
                <span>Annual Target:</span>
                <span>£{parseFloat(settings.annual_profit_target || 0).toFixed(2)}</span>
              </div>
              <div className="calc-row">
                <span>Weekly Target:</span>
                <span>£{parseFloat(settings.weekly_profit_target || 0).toFixed(2)}</span>
              </div>
              <div className="calc-row highlight">
                <span>Per Minute:</span>
                <span>£{parseFloat(settings.owner_profit_per_minute || 0).toFixed(4)}</span>
              </div>
            </div>
          )}
        </SettingsCard>

        <SettingsCard
          title="🕒 Trading Hours"
          description="Set your salon's opening hours to calculate overhead distribution accurately"
          icon="🕒"
          complete={settings.total_working_hours > 0}
        >
          <SimpleTradingHours
            key="trading-hours-stable-component"
            onChange={handleTradingHoursChange}
          />
          
          {settings.total_working_hours > 0 && (
            <div className="calculation-preview">
              <div className="calc-row">
                <span>Working Days:</span>
                <span>{settings.total_working_days} days/week</span>
              </div>
              <div className="calc-row highlight">
                <span>Total Hours:</span>
                <span>{settings.total_working_hours} hrs/week</span>
              </div>
            </div>
          )}
        </SettingsCard>

        <SettingsCard
          title="🏠 Overhead Costs"
          description="Your overhead costs are calculated from the Overheads tab and distributed per minute"
          icon="🏠"
          complete={settings.annual_overhead > 0}
        >
          <div className="overhead-summary">
            <div className="calc-row">
              <span>Annual Overhead:</span>
              <span>£{parseFloat(settings.annual_overhead || 0).toFixed(2)}</span>
            </div>
            <div className="calc-row highlight">
              <span>Per Minute:</span>
              <span>£{parseFloat(settings.overhead_per_minute || 0).toFixed(4)}</span>
            </div>
          </div>
          
          <button 
            className="secondary-button"
            onClick={handleNavigateToOverheads}
          >
            Manage Overheads
          </button>
          
          <small>Add your rent, utilities, insurance, and other fixed costs in the Overheads section.</small>
        </SettingsCard>

        <SettingsCard
          title="📈 Profit Margins"
          description="Set your standard and premium service profit margins"
          icon="📈"
          complete={settings.profit_level_1 && settings.profit_level_2}
        >
          <div className="card-form-group">
            <label>Standard Profit Margin (%) *</label>
            <input
              type="number"
              value={settings.profit_level_1}
              onChange={(e) => handleInputChange('profit_level_1', e.target.value)}
              placeholder="e.g. 10"
            />
            <small>For regular services</small>
          </div>
          
          <div className="card-form-group">
            <label>Premium Profit Margin (%) *</label>
            <input
              type="number"
              value={settings.profit_level_2}
              onChange={(e) => handleInputChange('profit_level_2', e.target.value)}
              placeholder="e.g. 20"
            />
            <small>For premium services</small>
          </div>
        </SettingsCard>

        <SettingsCard
          title="📅 Scheduling Settings"
          description="Configure your working weeks and staff utilization rates"
          icon="📅"
          complete={settings.weeks_per_year && settings.staff_utilization_rate}
        >
          <div className="card-form-group">
            <label>Working Weeks per Year *</label>
            <input
              type="number"
              value={settings.weeks_per_year}
              onChange={(e) => handleInputChange('weeks_per_year', e.target.value)}
              placeholder="e.g. 50"
            />
            <small>Typically 48-52 weeks</small>
          </div>
          
          <div className="card-form-group">
            <label>Staff Utilization Rate (%) *</label>
            <input
              type="number"
              value={settings.staff_utilization_rate}
              onChange={(e) => handleInputChange('staff_utilization_rate', e.target.value)}
              placeholder="e.g. 80"
              min="1"
              max="100"
            />
            <small>% of time spent on paid services</small>
          </div>
          
          {settings.weeks_per_year && settings.staff_utilization_rate && (
            <div className="calculation-preview">
              <div className="calc-row">
                <span>Annual Hours:</span>
                <span>{parseFloat(settings.total_annual_hours || 0).toFixed(0)} hrs</span>
              </div>
              <div className="calc-row highlight">
                <span>Effective Hours:</span>
                <span>{parseFloat(settings.effective_hours || 0).toFixed(0)} hrs</span>
              </div>
            </div>
          )}
        </SettingsCard>

        <SettingsCard
          title="💷 VAT Settings"
          description="Configure your VAT registration and rate"
          icon="💷"
          complete={true}
        >
          <div className="card-form-group">
            <label>VAT Rate (%)</label>
            <input
              type="number"
              value={settings.vat_rate}
              onChange={(e) => handleInputChange('vat_rate', e.target.value)}
              placeholder="20"
            />
            <small>Usually 20% in the UK</small>
          </div>
          
          <div className="card-form-group checkbox">
            <label>
              <input
                type="checkbox"
                checked={settings.is_vat_registered}
                onChange={(e) => handleInputChange('is_vat_registered', e.target.checked)}
              />
              Business is VAT Registered
            </label>
          </div>
        </SettingsCard>

      </div>

      <div className="settings-actions">
        {!saveSuccess && (settings.monthly_profit_target || settings.profit_level_1 || settings.profit_level_2) && (
          <div style={{
            padding: '15px',
            backgroundColor: '#fff3cd',
            border: '2px solid #ffc107',
            borderRadius: '8px',
            marginBottom: '15px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#856404', marginBottom: '8px' }}>
              ⚠️ Don't Forget to Save!
            </div>
            <p style={{ margin: '0', color: '#856404' }}>
              You've entered some settings but haven't saved them yet. 
              <strong> Click the button below to save your progress!</strong>
            </p>
          </div>
        )}

        <button 
          className={`save-button ${isComplete ? 'complete' : ''}`}
          onClick={saveAllSettings}
          disabled={loading}
          style={{ fontSize: '16px', fontWeight: 'bold', minHeight: '50px' }}
        >
          {loading ? (
            <span>💾 Saving...</span>
          ) : isComplete ? (
            <span>✅ Save Complete Settings & Continue</span>
          ) : (
            <span>💾 Save My Business Settings</span>
          )}
        </button>

        {!saveSuccess && (
          <div style={{
            marginTop: '15px',
            padding: '12px',
            backgroundColor: '#e8f4fd',
            border: '1px solid #bee5eb',
            borderRadius: '6px',
            fontSize: '14px',
            textAlign: 'center'
          }}>
            <strong>💡 Important:</strong> Your settings are only stored temporarily until you click "Save". 
            Save now to make them permanent and unlock the rest of your salon calculator!
          </div>
        )}
        
        {isComplete && saveSuccess && (
          <div style={{
            marginTop: '15px',
            padding: '15px',
            backgroundColor: '#d4edda',
            border: '2px solid #28a745',
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#155724', marginBottom: '8px' }}>
              🎉 Foundation Complete!
            </div>
            <p style={{ margin: '0', color: '#155724' }}>
              Your business settings are saved! You can now create accurate service pricing and use all calculator features.
            </p>
            <button
              onClick={() => navigate('/dashboard')}
              style={{
                marginTop: '10px',
                padding: '8px 16px',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              🚀 Go to Dashboard
            </button>
          </div>
        )}

        {saveSuccess && !isComplete && (
          <div style={{
            marginTop: '15px',
            padding: '12px',
            backgroundColor: '#d4edda',
            border: '1px solid #28a745',
            borderRadius: '6px',
            textAlign: 'center'
          }}>
            <strong>✅ Settings Saved!</strong> Complete the remaining fields above to unlock all features.
          </div>
        )}
      </div>
    </div>
  );
};

export default BusinessSettings;